// Thin fetch wrapper. Auth0 bearer token is injected per request by the hooks
// in hooks.ts, so this module stays framework-free.

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

// SPA and API live on different origins in production. In dev, Vite proxies
// /api/* → http://localhost:8790, so relative paths work; in prod we prefix
// the deployed Worker URL from VITE_API_BASE.
export function apiUrl(path: string): string {
  const base = import.meta.env.VITE_API_BASE ?? "";
  if (!base) return path;
  if (/^https?:\/\//i.test(path)) return path;
  return `${base.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
}

export async function apiFetch<T>(
  path: string,
  opts: { token?: string; method?: string; body?: unknown; space?: string } = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (opts.token) headers.authorization = `Bearer ${opts.token}`;
  if (opts.body) headers["content-type"] = "application/json";
  if (opts.space) headers["x-space"] = opts.space;

  const res = await fetch(apiUrl(path), {
    method: opts.method ?? "GET",
    headers,
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });

  // If we got HTML back the request hit the SPA host instead of the API
  // (typical when VITE_API_BASE isn't set or is wrong). Fail loudly so the
  // shape mismatch doesn't propagate as `undefined.field` deep in render.
  const ct = res.headers.get("content-type") ?? "";
  if (ct.includes("text/html")) {
    throw new ApiError(
      res.status,
      `Expected JSON from API but got HTML — check VITE_API_BASE (${import.meta.env.VITE_API_BASE ?? "(empty)"})`,
      undefined,
    );
  }

  const text = await res.text();
  const parsed = text ? safeJson(text) : undefined;
  if (!res.ok) throw new ApiError(res.status, errorMessage(res, parsed), parsed);
  return parsed as T;
}

function safeJson(s: string): unknown {
  try { return JSON.parse(s); } catch { return s; }
}

// "403 Forbidden" não diz nada a quem ficou travado na tela. O Worker sempre
// responde `{error, hint?}` — trazer isso para a mensagem é o que separa "você
// não é membro deste espaço" de "não deu para confirmar seu e-mail no Auth0".
function errorMessage(res: Response, body: unknown): string {
  const b = body as { error?: unknown; hint?: unknown; detail?: unknown } | undefined;
  const code = typeof b?.error === "string" ? b.error : undefined;
  const hint = typeof b?.hint === "string" ? b.hint : undefined;
  // `detail` (ex.: userinfo_status_401) é o que distingue as causas quando o
  // usuário só consegue mandar print da tela — vale ficar visível.
  const detail = typeof b?.detail === "string" ? b.detail : undefined;
  const head = `${res.status} ${code ?? res.statusText}`;
  return [hint ? `${head} — ${hint}` : head, detail ? `(${detail})` : ""].filter(Boolean).join(" ");
}

// Upload binário (anexos de nó). O Worker espera o corpo cru + x-file-name.
export async function apiUpload<T>(path: string, file: File, token: string): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: {
      authorization: `Bearer ${token}`,
      "x-file-name": encodeURIComponent(file.name),
      "content-type": file.type || "application/octet-stream",
    },
    body: file,
  });
  const text = await res.text();
  const parsed = text ? safeJson(text) : undefined;
  if (!res.ok) throw new ApiError(res.status, errorMessage(res, parsed), parsed);
  return parsed as T;
}

// Download autenticado como Blob (o Worker streama o objeto do R2).
export async function apiDownload(path: string, token: string): Promise<Blob> {
  const res = await fetch(apiUrl(path), { headers: { authorization: `Bearer ${token}` } });
  if (!res.ok) throw new ApiError(res.status, `${res.status} ${res.statusText}`, undefined);
  return res.blob();
}
